import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ConstraintConfigType } from 'types/ConstraintsTypes';
import { ProductConstraints, getDefaultProductConstraints, calculateSummary } from 'types/ProductTypes';
import constraintsStore from 'shared/store/constraintsStore';
import activityPanelStore from 'shared/store/activityPanelStore';
import { FinecoButton } from 'pages/assistant/components/FinecoButton';

import styles from './ProductContent.module.scss';

type Props = {
  configData: ConstraintConfigType;
};

export function ProductContent({ configData }: Props) {
  const { t } = useTranslation();

  const { openProductModal } = activityPanelStore();

  const product =
    (constraintsStore((s) => s.workingDocument?.product) as unknown as ProductConstraints | undefined) ??
    getDefaultProductConstraints();

  // Handle save from modal
  const handleModalSave = useCallback((data: ProductConstraints) => {
    constraintsStore.getState().applyGroupEdits('product', data as unknown as Record<string, unknown>);
  }, []);

  // Open modal via store (renders at top level)
  const handleOpenModal = useCallback(() => {
    openProductModal(product, handleModalSave);
  }, [product, handleModalSave, openProductModal]);

  // Calculate summary counts
  const summary = calculateSummary(product);

  return (
    <div className={styles.container}>
      {/* Summary Section */}
      <div className={styles.summarySection}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>{t('constraints.product.summary.lists')}</span>
          <span className={styles.summaryValue}>{summary.numLists}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>{t('constraints.product.summary.constrainedProducts')}</span>
          <span className={styles.summaryValue}>{summary.numConstrainedProducts}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>{t('constraints.product.summary.totalProducts')}</span>
          <span className={styles.summaryValue}>{summary.totalProducts}</span>
        </div>
      </div>

      {/* Gestisci Button */}
      <div className={styles.buttonContainer}>
        <FinecoButton click={handleOpenModal} copy={t('constraints.product.manage')} variant="ghost" />
      </div>
    </div>
  );
}

ProductContent.displayName = 'ProductContent';
